import { useEffect } from "react";
import { useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet.markercluster";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";

// Shared layer metadata (colors + labels) — used here for pins and by the
// panel for the toggle chips so the two always stay in sync.
export const LAYER_META = {
  restaurant: { color: "#f97316", label: "Restaurants" },
  bike_shop: { color: "#06b6d4", label: "Bike Shops" },
  coffee: { color: "#a16207", label: "Coffee Shops" },
  brewery: { color: "#a855f7", label: "Breweries" },
  hotel: { color: "#3b82f6", label: "Hotels" },
};

const esc = (s) =>
  String(s == null ? "" : s).replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));

function popupHtml(b, readOnly = false) {
  const meta = LAYER_META[b.layer] || {};
  const rows = [`<div style="font-weight:700;font-size:13px">${esc(b.name)}</div>`];
  let sub = meta.label || b.layer;
  if (!readOnly && b.layer === "bike_shop")
    sub += b.shop_id
      ? (b.partner
          ? ' · <span style="color:#16a34a;font-weight:600">Velopaths Partner</span>'
          : ' · <span style="color:#d97706">Prospect</span>')
      : ' · <span style="color:#6b7280">Not in CRM</span>';
  rows.push(`<div style="font-size:11px;color:#6b7280;text-transform:capitalize">${sub}</div>`);
  if (b.address) rows.push(`<div style="font-size:12px;margin-top:5px;color:#374151">${esc(b.address)}</div>`);
  if (b.phone) rows.push(`<div style="font-size:12px;margin-top:2px"><a href="tel:${esc(b.phone)}">${esc(b.phone)}</a></div>`);
  if (b.website) {
    const w = /^https?:/i.test(b.website) ? b.website : `https://${b.website}`;
    rows.push(`<div style="font-size:12px;margin-top:2px"><a href="${esc(w)}" target="_blank" rel="noopener noreferrer">Visit website</a></div>`);
  }
  if (b.distance_miles != null)
    rows.push(`<div style="font-size:11px;color:#9ca3af;margin-top:5px">${b.distance_miles} mi from trail</div>`);
  if (!readOnly && b.layer === "bike_shop" && b.shop_id) {
    const btn = (act, label) =>
      `<button data-biz-action="${act}" data-shop-id="${esc(b.shop_id)}" style="font-size:11px;margin:4px 4px 0 0;padding:3px 8px;border:1px solid #d1d5db;border-radius:6px;background:#fff;color:#111;cursor:pointer">${label}</button>`;
    rows.push(
      `<input data-biz-note placeholder="Optional note (e.g. Left voicemail)" style="width:100%;box-sizing:border-box;font-size:11px;margin-top:6px;padding:3px 6px;border:1px solid #d1d5db;border-radius:6px" />`
    );
    rows.push(
      `<div style="margin-top:4px;display:flex;flex-wrap:wrap">${btn("contacted", "Mark contacted")}${btn("prospect", "Mark prospect")}${btn("partner", "Invite to partner")}${btn("open_crm", "Open shop record")}</div>`
    );
  } else if (!readOnly && b.layer === "bike_shop") {
    rows.push(
      `<div style="margin-top:6px"><button data-biz-action="add_to_crm" style="font-size:11px;padding:4px 10px;border:1px solid #06b6d4;border-radius:6px;background:#06b6d4;color:#fff;cursor:pointer">+ Add to CRM</button></div>`
    );
  }
  return `<div style="min-width:190px">${rows.join("")}</div>`;
}

function pinIcon(color) {
  return L.divIcon({
    className: "biz-pin",
    html: `<span style="display:block;width:14px;height:14px;border-radius:50%;background:${color};border:2px solid #fff;box-shadow:0 0 0 1px rgba(0,0,0,.4)"></span>`,
    iconSize: [14, 14],
    iconAnchor: [7, 7],
    popupAnchor: [0, -8],
  });
}

// Renders all business pins in a single clustered layer group. Clusters at low
// zoom, expands to individual pins as you zoom in — keeps dense urban corridors
// and long trails performant. Rebuilds whenever the visible set changes.
export default function BusinessLayers({ businesses = [], onShopAction, readOnly = false }) {
  const map = useMap();
  useEffect(() => {
    if (!map) return undefined;
    const cluster = L.markerClusterGroup({
      chunkedLoading: true,
      maxClusterRadius: 50,
      showCoverageOnHover: false,
    });
    businesses.forEach((b) => {
      if (b.lat == null || b.lng == null) return;
      const mk = L.marker([b.lat, b.lng], { icon: pinIcon((LAYER_META[b.layer] || {}).color || "#64748b") });
      mk.bindPopup(popupHtml(b, readOnly));
      if (!readOnly && b.layer === "bike_shop" && onShopAction) {
        mk.on("popupopen", (ev) => {
          const el = ev.popup.getElement();
          if (!el) return;
          el.querySelectorAll("[data-biz-action]").forEach((btnEl) => {
            btnEl.onclick = () => {
              const noteEl = el.querySelector("[data-biz-note]");
              onShopAction(btnEl.getAttribute("data-shop-id"), btnEl.getAttribute("data-biz-action"), noteEl ? noteEl.value : "", b);
            };
          });
        });
      }
      cluster.addLayer(mk);
    });
    map.addLayer(cluster);
    return () => {
      try { map.removeLayer(cluster); } catch (e) { /* map torn down */ }
    };
  }, [map, businesses, onShopAction]);
  return null;
}
